import React, { useState, useEffect, useMemo } from "react";
import { TemplateList } from "../../../services/Templates";
import {
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
  FaEye,
} from "react-icons/fa";
import Select, { components } from "react-select";
import { Modal, Button } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import TemplatePreviewModal from "../Negotiation/components/TemplatePreviewModal";

const UserTemplates = () => {
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({
    value: "All",
    label: "All Categories",
  });
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true); // initial loading true
  const [activeTab, setActiveTab] = useState("negotiation");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);

        const response = await TemplateList();

        if (response.data?.data && Array.isArray(response.data.data)) {
          const apiCategories = response.data.data.map((c) => ({
            id: c.id,
            value: c.category_name,
            label: c.category_name,
          }));

          setCategories([
            { id: "all", value: "All", label: "All Categories" },
            ...apiCategories,
          ]);

          const apiTemplates = response.data.data.flatMap((category) =>
            category.templates.map((template) => ({
              uuid: template.uuid,
              title: template.title,
              desc: template.description || "",
              category: category.category_name,
              tags: template.tags || [],
              icon: "📄",
              image_path: template.image_path,
              html_content: template.html_content || "",
              is_negotiable: template.is_negotiable,
            }))
          );

          setTemplates(apiTemplates);
        } else {
          setError("No categories available.");
        }
      } catch (err) {
        setError("Failed to fetch categories and templates.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);


  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const searchRegex = useMemo(() => {
    if (!search) return null;
    const escapedSearch = escapeRegExp(search);
    return new RegExp(`(${escapedSearch})`, "gi");
  }, [search]);

  const highlightText = (text, regex) => {
    if (!regex || !text) return text;
    return text.split(regex).map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="templates__highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
  };

  const handleClosePreview = () => {
    setSelectedTemplate(null);
  };

  const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? (
        <FaChevronUp size={16} style={{ color: "#fff" }} />
      ) : (
        <FaChevronDown size={16} style={{ color: "#fff" }} />
      )}
    </components.DropdownIndicator>
  );


  const filteredTemplates = templates.filter((t) => {
    const matchCategory =
      !selectedCategory ||
      selectedCategory.value === "All" ||
      t.category === selectedCategory.value;

    const searchLower = search.toLowerCase();
    const matchSearch =
      (t.title || "").toLowerCase().includes(searchLower) ||
      (t.desc || "").toLowerCase().includes(searchLower) ||
      (t.category || "").toLowerCase().includes(searchLower) ||
      (t.tags &&
        t.tags.some((tag) => (tag || "").toLowerCase().includes(searchLower)));

    const matchTab = activeTab === "negotiation" ? t.is_negotiable : !t.is_negotiable;

    return matchCategory && matchSearch && matchTab;
  });

  useEffect(() => {
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);


  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <div className="templates">
        <div className="templates__container">
          {error && (
            <div className="templates__alert templates__alert--error">
              {error}
            </div>
          )}

          <div className="templates__header ">
            <div className="d-flex justify-content-between mb-3">
              <div className="templates__header-top">
                <h1 className="templates__page-title">{selectedCategory.label}</h1>
              </div>

              <div className="templates__tabs">
                <button
                  className={`templates__tab-btn ${activeTab === "negotiation" ? "active" : ""}`}
                  onClick={() => setActiveTab("negotiation")}
                >
                  Negotiable
                </button>
                <button
                  className={`templates__tab-btn ${activeTab === "non-negotiation" ? "active" : ""}`}
                  onClick={() => setActiveTab("non-negotiation")}
                >
                  Non-Negotiable
                </button>
              </div>
            </div>
            <div className="negotiation-template-filter-container">
              <div className="negotiation-template-search-box">
                <FaSearch className="negotiation-template-search-icon" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="negotiation-template-search-input"
                />
              </div>

              <div className="negotiation-template-dropdown">
                <Select
                  classNamePrefix="negotiation-template-select"
                  value={selectedCategory}
                  onChange={(selectedOption) =>
                    setSelectedCategory(
                      selectedOption || { value: "All", label: "All Categories" }
                    )
                  }
                  options={categories}
                  components={{
                    DropdownIndicator,
                    IndicatorSeparator: () => null,
                  }}
                  isClearable={false}
                  isSearchable={false}
                />

              </div>
            </div>
          </div>

          <div className="templates__grid">
            {filteredTemplates.length > 0 ? (
              filteredTemplates.map((t) => (
                <div key={t.uuid} className="templates__card">
                  <div
                    className="templates__card-content"
                  >
                    <div className="templates__icon-box">
                      {t.image_path ? (
                        <img
                          src={t.image_path}
                          alt={t.title || "Template"}
                          className="templates__icon-img"
                        />
                      ) : (
                        <span className="templates__icon-emoji">{t.icon}</span>
                      )}
                    </div>

                    <h3 className="templates__title">
                      {highlightText(t.title, searchRegex)}
                    </h3>

                    <p
                      className="templates__description clamp-3-lines mb-2"
                      data-toggle="tooltip"
                      data-bs-placement="top"
                      title={t.desc}
                    >
                      {highlightText(t.desc, searchRegex)}
                    </p>

                    {t.tags && t.tags.length > 0 && (
                      <div className="templates__tags">
                        {t.tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="templates__tag">
                            {highlightText(tag, searchRegex)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <button className="templates__preview-button" onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(t);
                  }}
                    aria-label="Preview template">
                    <FaEye className="templates__preview-button-icon" />
                    Preview
                  </button>
                </div>
              ))
            ) : (
              <div className="templates__empty">
                <FaInfoCircle className="templates__empty-icon" />
                <p className="templates__empty-text">
                  No templates available for the selected category.
                </p>
              </div>
            )}
          </div>
        </div>

        <TemplatePreviewModal
          show={!!selectedTemplate}
          onClose={handleClosePreview}
          template={selectedTemplate}
        />

      </div>
    </>
  );
};

export default UserTemplates;
